import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { callApi } from "../services/api.service";
import { examConfigKeys } from "./useExamConfig";

const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;

// ── Request / Response types ──────────────────────────────────────────────────

export interface GenerateQuestionRequest {
  skill_name: string;
  language: string;
  previous_questions: string[];
  translate_text?: string;
  forced_type?: 'spoken' | 'code-read-only' | 'code-write';
  section_name?: string;
  existing_types?: string[];
}

export interface GenerateQuestionResponse {
  question_text: string;
  type: "spoken" | "code-read-only" | "code-write";
  time_minutes: number;
  code_snippet?: string;
  coding_language?: string;
}

export interface AssignSkillRequest {
  question_text: string;
  available_skills: Array<{ id: string; name: string }>;
  section_name?: string;
}

export interface AssignSkillResponse {
  skill_id: string;
  confidence: number;
  suggested_new_skill?: string;
  suggested_type?: string;  // LLM's recommended modality: "spoken", "code-read-only", or "code-write"
}

export interface BulkRegenerateRequest {
  new_language: string;
}

export interface BulkRegenerateResponse {
  config_data: Record<string, unknown>;
  version: number;
  language: string;
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

/**
 * Generates a single AI question for a given skill and language.
 * Accepts previous_questions to avoid duplicate phrasing.
 */
export const useGenerateQuestion = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();

  return useMutation<GenerateQuestionResponse, Error, GenerateQuestionRequest>({
    mutationFn: async (req: GenerateQuestionRequest) => {
      const accessToken = await getAccessTokenSilently();
      const response = await callApi<GenerateQuestionResponse>(
        `${apiServerUrl}/api/jobs/offers/${jobId}/exam-config/generate-question`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(req),
        },
      );
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data as GenerateQuestionResponse;
    },
  });
};

/**
 * Assigns a custom question to the best-matching skill via LLM.
 * Returns the skill_id from available_skills that best fits the question text.
 */
export const useAssignSkill = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();

  return useMutation<AssignSkillResponse, Error, AssignSkillRequest>({
    mutationFn: async (req: AssignSkillRequest) => {
      const accessToken = await getAccessTokenSilently();
      const response = await callApi<AssignSkillResponse>(
        `${apiServerUrl}/api/jobs/offers/${jobId}/exam-config/assign-skill`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(req),
        },
      );
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data as AssignSkillResponse;
    },
  });
};

/**
 * Bulk-regenerates all AI questions in the exam config for a new language.
 * Invalidates the exam config cache on success so the editor re-fetches.
 */
export const useBulkRegenerate = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation<BulkRegenerateResponse, Error, BulkRegenerateRequest>({
    mutationFn: async (req: BulkRegenerateRequest) => {
      const accessToken = await getAccessTokenSilently();
      const response = await callApi<BulkRegenerateResponse>(
        `${apiServerUrl}/api/jobs/offers/${jobId}/exam-config/bulk-regenerate`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(req),
        },
      );
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data as BulkRegenerateResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: examConfigKeys.detail(jobId) });
    },
  });
};
